import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import { CustomersKeyframes, PlusIcon, SearchIcon } from '../containers/customer/CustomersUI';
import {
  EmptyState,
  LoadingLine,
  PrinterIcon,
  SearchField,
  TagIcon,
  UsersIcon,
} from '../containers/menu/MenuUI';

import UserList from '../containers/admin/UserList';
import UserDialog from '../containers/admin/UserDialog';
import CategoryList from '../containers/admin/CategoryList';
import CategoryDialog from '../containers/admin/CategoryDialog';
import { BoxIcon } from '../containers/stock/StockIcons';
import PrinterList from '../containers/admin/PrinterList';
import PrinterDialog from '../containers/admin/PrinterDialog';
import StationList from '../containers/admin/StationList';
import StationDialog from '../containers/admin/StationDialog';
import PublicMenuPanel from '../containers/publicmenu/PublicMenuPanel';
import SettingsPanel from '../containers/settings/SettingsPanel';
import ConfirmDialog from '../containers/admin/ConfirmDialog';

import {
  createUser,
  deleteUser as deleteUserRequest,
  fetchUsers,
  updateUser as updateUserRequest,
} from '../containers/admin/adminApi';
import {
  createCategory,
  deleteCategory as deleteCategoryRequest,
  fetchMenu,
  updateCategory as updateCategoryRequest,
} from '../containers/menu/menuApi';
import {
  createPrinter,
  deletePrinter as deletePrinterRequest,
  fetchPrinters,
  updatePrinter as updatePrinterRequest,
} from '../containers/printing/printersApi';
import {
  createStation,
  deleteStation as deleteStationRequest,
  fetchStations,
  updateStation as updateStationRequest,
} from '../containers/admin/stationsApi';
// Stations live in the stock slice: the Stock screen reads them for its Usage
// picker, and one list means a station added here shows up there without a
// reload.
import { setStations, setStationDataFetched } from '../containers/stock/stockSlice';
import { printTestSlip } from '../containers/printing/printJobs';

import {
  addUser,
  deleteUser,
  setUserDataFetched,
  setUsers,
  updateUser,
} from '../containers/user/userSlice';
import {
  addCategory,
  removeCategory,
  setCategories,
  setCategoryDataFetched,
  updateCategory,
} from '../containers/orders/categorySlice';
import { setProductDataFetched, setProducts } from '../containers/orders/productSlice';
import {
  addPrinter,
  removePrinter,
  setPrinters,
  setPrintersDataFetched,
  updatePrinter,
} from '../containers/printing/printerSlice';

/**
 * Administration — the three lists that decide how the rest of the till
 * behaves.
 *
 * Staff, which is the phone's drawer > Users. Categories and printers, which
 * together are the phone's Categories and Printers screens: a printer is a
 * machine on the shop WiFi, a category points at one, and that mapping is what
 * decides where a kitchen ticket comes out.
 *
 * All three are setup rather than service, all three are managers' work, and
 * all three are short lists — so they share one screen with a tab between
 * them instead of three sidebar entries.
 */

const TABS = [
  { id: 'users', label: 'Users' },
  { id: 'categories', label: 'Categories' },
  { id: 'printers', label: 'Printers' },
  { id: 'stations', label: 'Stations' },
  { id: 'publicmenu', label: 'Public menu' },
  { id: 'settings', label: 'Settings' },
];

/**
 * The tabs that are a searchable list you can add to.
 *
 * Public menu and Settings are not. One is a QR code and a switch, the other
 * is a form, so the search box and the Ku Dar button are hidden while either
 * is open rather than sitting there doing nothing.
 */
const LIST_TABS = ['users', 'categories', 'printers', 'stations'];

export default function Adminstration() {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const { users, isUserDataFetched } = useSelector((state) => state.users);
  const { categories, isCategoryDataFetched } = useSelector((state) => state.categories);
  const { products, isProductsDataFetched } = useSelector((state) => state.products);
  const { printers, isPrintersDataFetched } = useSelector((state) => state.printers);
  const { stations, isStationDataFetched } = useSelector((state) => state.stock);

  const [tab, setTab] = React.useState('users');
  const [query, setQuery] = React.useState('');
  const [notice, setNotice] = React.useState(null);

  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');

  const [editingUser, setEditingUser] = React.useState(null);
  const [creatingUser, setCreatingUser] = React.useState(false);
  const [deletingUser, setDeletingUser] = React.useState(null);

  const [editingCategory, setEditingCategory] = React.useState(null);
  const [creatingCategory, setCreatingCategory] = React.useState(false);
  const [deletingCategory, setDeletingCategory] = React.useState(null);

  const [editingPrinter, setEditingPrinter] = React.useState(null);
  const [creatingPrinter, setCreatingPrinter] = React.useState(false);
  const [deletingPrinter, setDeletingPrinter] = React.useState(null);
  const [busyPrinterId, setBusyPrinterId] = React.useState(null);

  const [editingStation, setEditingStation] = React.useState(null);
  const [creatingStation, setCreatingStation] = React.useState(false);
  const [deletingStation, setDeletingStation] = React.useState(null);

  const isManager = activeUser?.role === 'manager';
  const currentUserId = activeUser?._id || activeUser?.id;

  /** A line that fades itself. Used for the things that have no dialog. */
  const say = React.useCallback((message, isError) => {
    setNotice({ message, isError, at: Date.now() });
  }, []);

  React.useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), notice.isError ? 6000 : 3500);
    return () => clearTimeout(timer);
  }, [notice]);

  /* ── staff ──────────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!isManager || !business?._id || isUserDataFetched) return;

    let cancelled = false;
    setLoading(true);

    fetchUsers({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setUsers(rows));
        dispatch(setUserDataFetched(true));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || 'Could not load users');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isManager, business, token, isUserDataFetched, dispatch]);

  /* ── categories, and the menu behind them ───────────────────────────── */

  React.useEffect(() => {
    if (!isManager || !business?._id) return;
    if (isCategoryDataFetched && isProductsDataFetched) return;

    let cancelled = false;

    fetchMenu({ businessId: business._id, token })
      .then(({ menuItems, categories: rows }) => {
        if (cancelled) return;
        dispatch(setCategories(rows));
        dispatch(setCategoryDataFetched(true));
        dispatch(setProducts(menuItems));
        dispatch(setProductDataFetched(true));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isManager, business, token, isCategoryDataFetched, isProductsDataFetched, dispatch]);

  /* ── printers ───────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!isManager || !business?._id || isPrintersDataFetched) return;

    let cancelled = false;

    fetchPrinters({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setPrinters(rows));
        dispatch(setPrintersDataFetched(true));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isManager, business, token, isPrintersDataFetched, dispatch]);

  React.useEffect(() => {
    if (!isManager || !business?._id || isStationDataFetched) return;

    fetchStations({ businessId: business._id, token })
      .then((rows) => {
        dispatch(setStations(rows));
        dispatch(setStationDataFetched(true));
      })
      // Silent, like the printers above: the tab shows its own empty state,
      // and a manager who never opens it should not be told about it.
      .catch(() => {});
  }, [isManager, business, token, isStationDataFetched, dispatch]);

  /* ── writing ────────────────────────────────────────────────────────── */

  const submitUser = async ({ user, payload }) => {
    if (user) {
      const saved = await updateUserRequest({ id: user._id, payload, token });
      dispatch(updateUser({ _id: user._id, updatedUser: saved || payload }));
      return;
    }
    const created = await createUser({ payload, token });
    dispatch(addUser(created));
  };

  const removeUser = async () => {
    await deleteUserRequest({ id: deletingUser._id, token });
    dispatch(deleteUser(deletingUser));
  };

  const submitCategory = async ({ category, payload }) => {
    if (category) {
      const saved = await updateCategoryRequest({ id: category._id, payload, token });
      dispatch(updateCategory({ _id: category._id, updated: saved || payload }));
      return;
    }
    const created = await createCategory({ payload, token });
    dispatch(addCategory(created));
  };

  const removeCategoryRow = async () => {
    await deleteCategoryRequest({ id: deletingCategory._id, token });
    dispatch(removeCategory(deletingCategory._id));
  };

  const submitPrinter = async ({ printer, payload }) => {
    if (printer) {
      const saved = await updatePrinterRequest({ id: printer._id, payload, token });
      dispatch(updatePrinter({ _id: printer._id, updated: saved || payload }));
      say(`${payload.name} saved`);
      return;
    }
    const created = await createPrinter({ payload, token });
    dispatch(addPrinter(created));
    say(`${payload.name} added`);
  };

  const removePrinterRow = async () => {
    await deletePrinterRequest({ id: deletingPrinter._id, token });
    dispatch(removePrinter(deletingPrinter._id));
    say(`${deletingPrinter.name} deleted`);
  };

  /**
   * Stations are re-read rather than patched into the list.
   *
   * The list is short, it is read once per session, and the server decides
   * things this side would have to guess — a delete is really a retirement,
   * and a duplicate name is refused. One extra request beats keeping a local
   * copy that can disagree with it.
   */
  const refreshStations = () => dispatch(setStationDataFetched(false));

  const submitStation = async ({ station, payload }) => {
    if (station) {
      await updateStationRequest({ id: station._id, payload, token });
      say(`${payload.name} saved`);
    } else {
      await createStation({ payload, token });
      say(`${payload.name} added`);
    }
    refreshStations();
  };

  const removeStationRow = async () => {
    const result = await deleteStationRequest({ id: deletingStation._id, token });
    // The server's own words: it retires rather than deletes, and says so.
    say(result.message || `${deletingStation.name} retired`);
    refreshStations();
  };

  /**
   * The slip that proves which machine an address belongs to.
   *
   * Only this row shows a spinner: a manager testing four printers should not
   * have the whole list freeze between each one.
   */
  const testPrinter = async (printer) => {
    setBusyPrinterId(printer._id);
    const result = await printTestSlip(printer);
    setBusyPrinterId(null);

    if (result.ok) say(`Test slip sent to ${printer.name}`);
    else say(result.message, true);
  };

  /* ── filtering ──────────────────────────────────────────────────────── */

  const visibleUsers = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (user) =>
        String(user.name || '').toLowerCase().includes(q) ||
        String(user.username || '').toLowerCase().includes(q)
    );
  }, [users, query]);

  const visibleCategories = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((category) =>
      String(category.name || '').toLowerCase().includes(q)
    );
  }, [categories, query]);

  const visiblePrinters = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return printers;
    return printers.filter(
      (printer) =>
        String(printer.name || '').toLowerCase().includes(q) ||
        String(printer.host || '').includes(q)
    );
  }, [printers, query]);

  const visibleStations = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return stations;
    return stations.filter((station) => String(station.name || '').toLowerCase().includes(q));
  }, [stations, query]);

  const itemCounts = React.useMemo(() => {
    const counts = {};
    products.forEach((item) => {
      if (!item || item.status === 'unavailable') return;
      const id =
        item.category && typeof item.category === 'object' ? item.category._id : item.category;
      if (!id) return;
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [products]);

  /* ── not for cashiers ───────────────────────────────────────────────── */

  if (!isManager) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <span style={{ fontSize: 14, color: T.inkSoft }}>
          Only a manager can open Administration.
        </span>
      </div>
    );
  }

  const onUsers = tab === 'users';
  const onCategories = tab === 'categories';
  const onPrinters = tab === 'printers';
  const onStations = tab === 'stations';
  const onPublicMenu = tab === 'publicmenu';
  const onSettings = tab === 'settings';
  const onList = LIST_TABS.indexOf(tab) !== -1;

  const rows = onUsers
    ? visibleUsers
    : onCategories
    ? visibleCategories
    : onPrinters
    ? visiblePrinters
    : visibleStations;

  const openAdd = () => {
    if (onUsers) setCreatingUser(true);
    else if (onCategories) setCreatingCategory(true);
    else if (onPrinters) setCreatingPrinter(true);
    else setCreatingStation(true);
  };

  const searchPlaceholder = onUsers
    ? 'Search staff'
    : onCategories
    ? 'Search categories'
    : onPrinters
    ? 'Search printers'
    : 'Search stations';

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      {/* ── which list, and how to add to it ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          padding: '18px 28px 0',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          {TABS.map((entry) => {
            const active = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => {
                  setTab(entry.id);
                  setQuery('');
                }}
                style={{
                  padding: '0 4px 14px',
                  margin: '0 16px 0 0',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${active ? T.brand : 'transparent'}`,
                  font: 'inherit',
                  fontSize: 15,
                  fontWeight: active ? 700 : 500,
                  color: active ? T.ink : T.inkFaint,
                  cursor: 'pointer',
                }}
              >
                {entry.label}
              </button>
            );
          })}
        </div>

        {/* Only the list tabs get a search box and an add button. */}
        <div
          style={{
            display: onList ? 'flex' : 'none',
            alignItems: 'center',
            gap: 12,
            paddingBottom: 12,
          }}
        >
          <div style={{ width: 250 }}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder}
              icon={<SearchIcon />}
            />
          </div>

          <button
            type="button"
            onClick={openAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 42,
              padding: '0 18px',
              background: T.brand,
              border: 'none',
              borderRadius: T.radiusPill,
              font: 'inherit',
              fontSize: 13.5,
              fontWeight: 600,
              color: T.surface,
              cursor: 'pointer',
            }}
          >
            <PlusIcon />
            <span>Ku Dar</span>
          </button>
        </div>
      </div>

      {/* ── the list ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 28px' }}>
        {onPublicMenu ? (
          /* One setting and one QR code, rather than a list. It borrows the
             same notice line the rest of this screen uses, so "saved" reads
             the same here as everywhere else. */
          <PublicMenuPanel onNotice={say} />
        ) : onSettings ? (
          /* The shop's own details. Same notice line again. */
          <SettingsPanel onNotice={say} />
        ) : onUsers && loadError ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: T.danger }}>{loadError}</span>
          </div>
        ) : onUsers && loading && !users.length ? (
          <LoadingLine />
        ) : rows.length ? (
          <div style={{ maxWidth: 820 }}>
            {onUsers ? (
              <UserList
                users={visibleUsers}
                currentUserId={currentUserId}
                onEdit={setEditingUser}
                onDelete={setDeletingUser}
              />
            ) : onCategories ? (
              <CategoryList
                categories={visibleCategories}
                printers={printers}
                itemCounts={itemCounts}
                onEdit={setEditingCategory}
                onDelete={setDeletingCategory}
              />
            ) : onPrinters ? (
              <PrinterList
                printers={visiblePrinters}
                busyId={busyPrinterId}
                onTest={testPrinter}
                onEdit={setEditingPrinter}
                onDelete={setDeletingPrinter}
              />
            ) : (
              <StationList
                stations={visibleStations}
                onEdit={setEditingStation}
                onDelete={setDeletingStation}
              />
            )}
          </div>
        ) : (
          <EmptyState
            icon={
              onUsers ? (
                <UsersIcon />
              ) : onCategories ? (
                <TagIcon />
              ) : onPrinters ? (
                <PrinterIcon size={24} />
              ) : (
                <BoxIcon />
              )
            }
            title={
              query.trim()
                ? 'Nothing found'
                : onUsers
                ? 'Ma jiro shaqaale la helay'
                : onCategories
                ? 'No categories found'
                : onPrinters
                ? 'No printers yet'
                : 'No stations yet'
            }
          />
        )}
      </div>

      {/* ── what just happened ── */}
      {notice ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 24,
            transform: 'translateX(-50%)',
            maxWidth: 460,
            padding: '11px 18px',
            background: notice.isError ? T.danger : T.ink,
            borderRadius: T.radiusPill,
            fontSize: 13,
            fontWeight: 600,
            color: T.surface,
            boxShadow: T.dialog,
          }}
        >
          {notice.message}
        </div>
      ) : null}

      {/* ── staff dialogs ── */}
      {creatingUser ? (
        <UserDialog
          businessId={business?._id}
          onClose={() => setCreatingUser(false)}
          onSubmit={submitUser}
        />
      ) : null}

      {editingUser ? (
        <UserDialog
          user={editingUser}
          businessId={business?._id}
          onClose={() => setEditingUser(null)}
          onSubmit={submitUser}
        />
      ) : null}

      {deletingUser ? (
        <ConfirmDialog
          title="Delete User"
          body={`Ma hubtaa inaad tirtirto ${deletingUser.name}?`}
          note="They stop appearing here and can no longer sign in. Their past orders are not touched."
          onClose={() => setDeletingUser(null)}
          onConfirm={removeUser}
        />
      ) : null}

      {/* ── category dialogs ── */}
      {creatingCategory ? (
        <CategoryDialog
          printers={printers}
          businessId={business?._id}
          onClose={() => setCreatingCategory(false)}
          onSubmit={submitCategory}
        />
      ) : null}

      {editingCategory ? (
        <CategoryDialog
          category={editingCategory}
          printers={printers}
          businessId={business?._id}
          onClose={() => setEditingCategory(null)}
          onSubmit={submitCategory}
        />
      ) : null}

      {deletingCategory ? (
        <ConfirmDialog
          title="Delete Category"
          body={`Ma hubtaa inaad tirtirto category-gan '${deletingCategory.name}'?`}
          note={
            itemCounts[deletingCategory._id]
              ? `${itemCounts[deletingCategory._id]} item(s) are in it. They stay on the menu, with no category and no printed ticket.`
              : 'This one is empty.'
          }
          onClose={() => setDeletingCategory(null)}
          onConfirm={removeCategoryRow}
        />
      ) : null}

      {/* ── printer dialogs ── */}
      {creatingPrinter ? (
        <PrinterDialog
          businessId={business?._id}
          onClose={() => setCreatingPrinter(false)}
          onSubmit={submitPrinter}
        />
      ) : null}

      {editingPrinter ? (
        <PrinterDialog
          printer={editingPrinter}
          businessId={business?._id}
          onClose={() => setEditingPrinter(null)}
          onSubmit={submitPrinter}
        />
      ) : null}

      {deletingPrinter ? (
        <ConfirmDialog
          title="Are you sure?"
          body={`Delete ${deletingPrinter.name}?`}
          note="Categories pointing at it must be moved to another printer first — the server will say how many."
          onClose={() => setDeletingPrinter(null)}
          onConfirm={removePrinterRow}
        />
      ) : null}

      {/* ── station dialogs ── */}
      {creatingStation ? (
        <StationDialog
          businessId={business?._id}
          onClose={() => setCreatingStation(false)}
          onSubmit={submitStation}
        />
      ) : null}

      {editingStation ? (
        <StationDialog
          station={editingStation}
          businessId={business?._id}
          onClose={() => setEditingStation(null)}
          onSubmit={submitStation}
        />
      ) : null}

      {deletingStation ? (
        <ConfirmDialog
          title="Retire this station?"
          body={`Retire ${deletingStation.name}?`}
          note="It stops appearing when stock is issued. Nothing already posted to it is touched — the reports keep reading correctly."
          confirmLabel="Retire"
          onClose={() => setDeletingStation(null)}
          onConfirm={removeStationRow}
        />
      ) : null}
    </div>
  );
}
